import { showErrorMessage, hideErrorMessage } from './errorHandler'
import { calculateAge } from './helpers'
import { debouncer } from './debouncer'

export function validatePromo (form: HTMLFormElement) {
    const validatePromoCallback = () => {
        const input = form.querySelector<HTMLInputElement>('[name="PromotionCodeId"]:not([readonly])');
        const promoCodeApiUrl = form.getAttribute("data-url");

        if (! input) {
            return;
        }
        const inputContainer = input.closest('fieldset');
        const messageContainer = inputContainer.querySelector<HTMLElement>('.selection-note');
        const messageWrapper = messageContainer.querySelector<HTMLElement>('.error-message');
        const code = input.value;

        // CP6WKFREE
        // check promo codes that are more than 3 characters in length
        if (code.length >= 3) {
            inputContainer.classList.remove('error');
            let endpoint = promoCodeApiUrl != null ? promoCodeApiUrl : "/umbraco/api/PromoCode/Validate?code=";
            fetch(`${endpoint}${code}`, {
                headers: {
                    Accept: 'application/json',
                }
            })
                // Retrieve its body as ReadableStream
                .then(response => {
                    if (response.status !== 200) {
                        throw Error(response.statusText);
                    }
                    return response;
                })
                .then(response => response.json())
                .then(jsonObject => {
                    input.classList.remove('border-red');
                    input.classList.remove('error');

                    messageContainer.classList.remove('border-red');
                    messageContainer.classList.add('border-orange');
                    messageContainer.classList.remove('hidden');
                    messageWrapper.innerHTML = jsonObject.AppliedMessage;
                })
                .catch(() => {
                    input.classList.add('border-red');
                    input.classList.add('error');

                    messageContainer.classList.add('border-red');
                    messageContainer.classList.remove('border-orange');
                    messageContainer.classList.remove('hidden');
                    messageWrapper.innerHTML = "Sorry! This promo code is invalid.";
                });
        } else {
            input.classList.remove('border-red');
            input.classList.remove('error');
            messageContainer.classList.add('hidden');
        }
    }

    const updateFormState = () => {
        debouncer(validatePromoCallback);
    }

    form.querySelectorAll("input, select")?.forEach((elem) => {
        elem.addEventListener("input", updateFormState);
    });

    updateFormState();
}

export function validateMaxDate (form: HTMLFormElement, isValidCallback?: Function) {
    const inputEvent = new Event('input');
    let firstLoad = true;

    const updateFormState = (event: Event) => {
        const input = event.target as HTMLInputElement | HTMLSelectElement;
        const dataGroup = input.getAttribute('data-dob-group');
        const maxDateInputElement = form.querySelector(`input[data-dob-group="${dataGroup}"][data-max-date], select[data-dob-group="${dataGroup}"][data-max-date]`) as HTMLInputElement | HTMLSelectElement;
        const maxDateArray = maxDateInputElement?.getAttribute('data-max-date') || '';
        const [
            maxYear,
            maxMonth,
            maxDay,
        ] = maxDateArray.split('-');

        if (maxDay.length != 2 || maxMonth.length != 2 || maxYear.length != 4) {
            return;
        }
        const maxDate = new Date(parseInt(maxYear), parseInt(maxMonth) - 1, parseInt(maxDay));
        const dayInput = form.querySelector(`[data-dob-group="${dataGroup}"][data-dob-type="day"]`) as HTMLInputElement | HTMLSelectElement;
        const monthInput = form.querySelector(`[data-dob-group="${dataGroup}"][data-dob-type="month"]`) as HTMLInputElement | HTMLSelectElement;
        const yearInput = form.querySelector(`[data-dob-group="${dataGroup}"][data-dob-type="year"]`) as HTMLInputElement | HTMLSelectElement;

        const selectedDay = dayInput?.value || '';
        const selectedMonth = monthInput?.value || '';
        const selectedYear = yearInput?.value || '';

        if ((selectedDay.length != 1 && selectedDay.length != 2) || selectedMonth.length != 2 || selectedYear.length != 4) {
            return;
        }
        const selectedDate = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1, parseInt(selectedDay));

        if(selectedDate.getDate() !== parseInt(selectedDay)){
            showErrorMessage(input, "Invalid date");
        }
        else if (selectedDate.getTime() < Date.now()) {
            showErrorMessage(input, "Must provide a future date.");
        } else if (selectedDate.getTime() > maxDate.getTime()) {
            showErrorMessage(input, maxDateInputElement.getAttribute('title'));
        } else {
            hideErrorMessage(dayInput);
            hideErrorMessage(monthInput);
            hideErrorMessage(yearInput);
            if(isValidCallback && !firstLoad){
                isValidCallback(selectedDate, input);
            }
        }
    }

    form.querySelectorAll("input[data-dob-group], select[data-dob-group]")?.forEach((elem) => {
        elem.addEventListener("input", updateFormState);

        elem.dispatchEvent(inputEvent);
    });
    firstLoad = false;
}

export function validateConcessionDate (form: HTMLFormElement) {
    const inputEvent = new Event('input');

    const updateFormState = (event: Event) => {
        const input = event.target as HTMLInputElement | HTMLSelectElement;
        const dataGroupAttribute = input.getAttribute('data-dob-group');
        const startDataGroup = (dataGroupAttribute.startsWith('main'))
            ? 'main-commencement'
            : 'partner-commencement';
        const endDataGroup = (dataGroupAttribute.startsWith('main'))
            ? 'main-end'
            : 'partner-end';

        const startDayInput = form.querySelector(`[data-dob-group="${startDataGroup}"][data-dob-type="day"]`) as HTMLInputElement | HTMLSelectElement;
        const startMonthInput = form.querySelector(`[data-dob-group="${startDataGroup}"][data-dob-type="month"]`) as HTMLInputElement | HTMLSelectElement;
        const startYearInput = form.querySelector(`[data-dob-group="${startDataGroup}"][data-dob-type="year"]`) as HTMLInputElement | HTMLSelectElement;
        const selectedStartDay = startDayInput?.value || '';
        const selectedStartMonth = startMonthInput?.value || '';
        const selectedStartYear = startYearInput?.value || '';
        const selectedStartDate = ((selectedStartDay.length === 1 || selectedStartDay.length === 2) && selectedStartMonth.length === 2 && selectedStartYear.length === 4)
            ? new Date(parseInt(selectedStartYear), parseInt(selectedStartMonth) - 1, parseInt(selectedStartDay))
            : null;

        const endDayInput = form.querySelector(`[data-dob-group="${endDataGroup}"][data-dob-type="day"]`) as HTMLInputElement | HTMLSelectElement;
        const endMonthInput = form.querySelector(`[data-dob-group="${endDataGroup}"][data-dob-type="month"]`) as HTMLInputElement | HTMLSelectElement;
        const endYearInput = form.querySelector(`[data-dob-group="${endDataGroup}"][data-dob-type="year"]`) as HTMLInputElement | HTMLSelectElement;
        const selectedEndDay = endDayInput?.value || '';
        const selectedEndMonth = endMonthInput?.value || '';
        const selectedEndYear = endYearInput?.value || '';
        const selectedEndDate = ((selectedEndDay.length === 1 || selectedEndDay.length === 2) && selectedEndMonth.length === 2 && selectedEndYear.length === 4)
            ? new Date(parseInt(selectedEndYear), parseInt(selectedEndMonth) - 1, parseInt(selectedEndDay))
            : null;

        const hideStartErrorMessages = () => {
            hideErrorMessage(startDayInput);
            hideErrorMessage(startMonthInput);
            hideErrorMessage(startYearInput);
        }

        const hideEndErrorMessages = () => {
            hideErrorMessage(endDayInput);
            hideErrorMessage(endMonthInput);
            hideErrorMessage(endYearInput);
        }

        const hideAllErrorMessages = () => {
            hideStartErrorMessages();
            hideEndErrorMessages();
        }

        hideAllErrorMessages();

        if(`${selectedStartYear}`.length != 4){
            showErrorMessage(startYearInput, "Invalid year");
            return;
        }
        if(`${selectedEndYear}`.length != 4){
            showErrorMessage(endYearInput, "Invalid year");
            return;
        }

        const now = Date.now();

        if (selectedEndDate !== null && (input === endDayInput || input === endMonthInput || input === endYearInput)) {
            if (selectedEndDate.getTime() < now) {
                showErrorMessage(input, "Please enter a valid expiry date.");
            } else if (selectedStartDate !== null && selectedStartDate.getTime() >= selectedEndDate.getTime()) {
                showErrorMessage(input, "Please enter a valid expiry date.");
            } else {
                hideEndErrorMessages();
            }
        } else if (selectedStartDate !== null && (input === startDayInput || input === startMonthInput || input === startYearInput)) {
            if (selectedEndDate !== null && selectedStartDate.getTime() >= selectedEndDate.getTime()) {
                showErrorMessage(input, "Please enter a valid commencement date");
            } else {
                hideStartErrorMessages();
            }
        } else {
            hideAllErrorMessages();
        }
    };

    form.querySelectorAll("input[data-dob-group], select[data-dob-group]")?.forEach((elem) => {
        elem.addEventListener("input", updateFormState);

        elem.dispatchEvent(inputEvent);
    });

}

export function validateCCDate (form: HTMLFormElement) {
    const inputEvent = new Event('input');

    const updateFormState = (event: Event) => {
        const input = event.target as HTMLInputElement | HTMLSelectElement;
        const dataGroup = input.getAttribute('data-dob-group');
        const monthInput = form.querySelector(`[data-dob-group="${dataGroup}"][data-dob-type="month"]`) as HTMLInputElement | HTMLSelectElement;
        const yearInput = form.querySelector(`[data-dob-group="${dataGroup}"][data-dob-type="year"]`) as HTMLInputElement | HTMLSelectElement;

        const isCCYear = yearInput.getAttribute('data-is-cc-year') === '1';

        const selectedMonth = monthInput?.value || '';
        const selectedYear = (isCCYear)
            ? `20${yearInput?.value || ''}`
            : yearInput?.value || '';


        if(`${selectedYear}`.length != 4){
            showErrorMessage(yearInput, "Invalid year");
        }

        if (selectedMonth.length != 2 || selectedYear.length != 4) {
            return;
        }
        const selectedDate = new Date(parseInt(selectedYear), parseInt(selectedMonth) - 1);


        if (selectedDate.getTime() < Date.now()) {
            showErrorMessage(input, "Must provide a future date.");
        } else {
            hideErrorMessage(monthInput);
            hideErrorMessage(yearInput);
        }
    }

    form.querySelectorAll("input[data-dob-group], select[data-dob-group]")?.forEach((elem) => {
        elem.addEventListener("input", updateFormState);

        elem.dispatchEvent(inputEvent);
    });
}

export function validateMedicare (form: { querySelector: (arg0: string) => HTMLInputElement; }) {
    // test card number 2428 77813 2/1
    // https://stackoverflow.com/a/64253953
    const input = form.querySelector('#MedicareNumber') as HTMLInputElement;
    const inputEvent = new Event('input');

    // https://stackoverflow.com/a/52324373
    const validateInput = () => {
        const medicare = input.value.replace(/[^\d]/g, '');
        let isValid = false;

        if (medicare && medicare.length === 10) {
            const matches = medicare.match(/^(\d{8})(\d)/);

            if (matches) {
                const base = matches[1];
                const checkDigit = matches[2];
                const weights = [1, 3, 7, 9, 1, 3, 7, 9];

                let sum = 0;
                for (let i = 0; i < weights.length; i++) {
                    sum += parseInt(base[i], 10) * weights[i];
                }

                isValid = sum % 10 === parseInt(checkDigit, 10);
            }
        }

        if (!isValid) {
            showErrorMessage(input, "Please enter a valid Medicare card number.");
        } else {
            hideErrorMessage(input);
        }
    }

    input.addEventListener("input", validateInput);
    input.dispatchEvent(inputEvent);
}

export function validateVisaCommencementDates (
    form: HTMLFormElement,
    errorMessage: string = "Date must be a future date",
    beforeDateErrorMessage: string = "Visa expiry should be after the commencement date."
) {

    const afterDayElem = form.querySelector(`[name='VisaExpiryDay']`) as HTMLInputElement;
    const afterMonthElem = form.querySelector(`[name='VisaExpiryMonth']`) as HTMLSelectElement;
    const afterYearElem = form.querySelector(`[name='VisaExpiryYear']`) as HTMLInputElement;
    const beforeDayElem = form.querySelector(`[name='VisaCommencementDay']`) as HTMLInputElement;
    const beforeMonthElem = form.querySelector(`[name='VisaCommencementMonth']`) as HTMLSelectElement;
    const beforeYearElem = form.querySelector(`[name='VisaCommencementYear']`) as HTMLInputElement;
    const inputEvent = new Event('input');

    const hideAllErrorMessages = (dayElem: HTMLInputElement | HTMLSelectElement, monthElem: HTMLInputElement | HTMLSelectElement, yearElem: HTMLInputElement | HTMLSelectElement) => {
        hideErrorMessage(dayElem);
        hideErrorMessage(monthElem);
        hideErrorMessage(yearElem);
    }
    const updateFormState = (event: Event) => {

        const target = event.target as HTMLInputElement | HTMLSelectElement;

        const beforeYearValue = beforeYearElem.value;
        const beforeDate = new Date(
            parseInt(beforeYearValue),
            parseInt(beforeMonthElem.value) - 1,
            parseInt(beforeDayElem.value));

        // Validate before year if length is not equal to 4
        if(`${beforeYearValue}`.length != 4){
            showErrorMessage(beforeYearElem, "Invalid year");
            return;
        }

        // Validate before date first
        if(beforeDate.getDate() !== parseInt(beforeDayElem.value)){
            showErrorMessage(beforeDayElem, "Invalid date");
        }
        // Validate before day if length is not equal to 2
        if(`${beforeDayElem.value}`.length > 2){
            showErrorMessage(beforeDayElem, "Invalid date");
            return;
        }
        else{
            hideAllErrorMessages(beforeDayElem, beforeMonthElem, beforeYearElem);
        }

        // Validate After date
        const now = Date.now();
        const day = afterDayElem.value;
        const month = afterMonthElem.value;
        const year = afterYearElem.value;

        if(day === '' && month === '' && year === ''){
            hideAllErrorMessages(afterDayElem, afterMonthElem, afterYearElem);
            return;
        }

        // Validate after year if length is not equal to 4
        if(`${year}`.length != 4){
            showErrorMessage(afterYearElem, "Invalid year");
            return;
        }

        const afterDate = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day));

        if(afterDate.getDate() !== parseInt(day)){
            showErrorMessage(afterDayElem, "Invalid date");
        }
        else if(afterDate.getTime() < now){
            showErrorMessage(target, errorMessage);
        }
        else if(afterDate.getTime() <= beforeDate.getTime()){
            showErrorMessage(target, beforeDateErrorMessage);
        }else if(day.length > 2){
            showErrorMessage(afterDayElem, "Invalid date");
        }
        else{
            hideAllErrorMessages(afterDayElem, afterMonthElem, afterYearElem);
        }
    };

    afterDayElem.addEventListener("input", updateFormState);
    afterDayElem.dispatchEvent(inputEvent);

    afterMonthElem.addEventListener("input", updateFormState);
    afterMonthElem.dispatchEvent(inputEvent);

    afterYearElem.addEventListener("input", updateFormState);
    afterYearElem.dispatchEvent(inputEvent);

    beforeDayElem.addEventListener("input", updateFormState);
    beforeDayElem.dispatchEvent(inputEvent);

    beforeMonthElem.addEventListener("input", updateFormState);
    beforeMonthElem.dispatchEvent(inputEvent);

    beforeYearElem.addEventListener("input", updateFormState);
    beforeYearElem.dispatchEvent(inputEvent);
};

export function validateIfFutureDate (
    form: HTMLFormElement,
    dayElementName: string,
    monthElementName: string,
    yearElementName: string,
    errorMessage: string = "Date must be a future date"
) {

    const dayElem = form.querySelector(`[name='${dayElementName}']`) as HTMLInputElement;
    const monthElem = form.querySelector(`[name='${monthElementName}']`) as HTMLSelectElement;
    const yearElem = form.querySelector(`[name='${yearElementName}']`) as HTMLInputElement;
    const inputEvent = new Event('input');

    const hideAllErrorMessages = () => {
        hideErrorMessage(dayElem);
        hideErrorMessage(monthElem);
        hideErrorMessage(yearElem);
    }
    const updateFormState = (event: Event) => {

        const target = event.target as HTMLInputElement | HTMLSelectElement;

        const now = Date.now();
        const day = dayElem.value;
        const month = monthElem.value;
        const year = yearElem.value;

        if(day === '' && month === '' && year === ''){
            hideAllErrorMessages();
            return;
        }

        const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

        if(parsedDate.getDate() !== parseInt(day)){
            showErrorMessage(dayElem, "Invalid date");
        }
        else if(parsedDate.getTime() < now){
            showErrorMessage(target, errorMessage);
        }
        else{
            hideAllErrorMessages();
        }


    };

    if(dayElem){
        dayElem.addEventListener("input", updateFormState);
        dayElem.dispatchEvent(inputEvent);
    }


    if(monthElem){
        monthElem.addEventListener("input", updateFormState);
        monthElem.dispatchEvent(inputEvent);

    }

    if(yearElem){
        yearElem.addEventListener("input", updateFormState);
        yearElem.dispatchEvent(inputEvent);
    }
};

export function validateDate (
    form: HTMLFormElement,
    dayElementName: string,
    monthElementName: string,
    yearElementName: string,
    errorMessage: string = "Invalid date") {

    const dayElem = form.querySelector(`[name='${dayElementName}']`) as HTMLInputElement;
    const monthElem = form.querySelector(`[name='${monthElementName}']`) as HTMLSelectElement;
    const yearElem = form.querySelector(`[name='${yearElementName}']`) as HTMLInputElement;
    const inputEvent = new Event('input');

    const hideAllErrorMessages = () => {
        hideErrorMessage(dayElem);
        hideErrorMessage(monthElem);
        hideErrorMessage(yearElem);
    }
    const updateFormState = () => {
        const day = dayElem.value;
        const month = monthElem.value;
        const year = yearElem.value;

        const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

        if(parsedDate.getDate() !== parseInt(day)){
            showErrorMessage(dayElem, errorMessage);
        }
        else{
            hideAllErrorMessages();
        }


    };

    if(dayElem){
        dayElem.addEventListener("input", updateFormState);
        dayElem.dispatchEvent(inputEvent);
    }


    if(monthElem){
        monthElem.addEventListener("input", updateFormState);
        monthElem.dispatchEvent(inputEvent);

    }

    if(yearElem){
        yearElem.addEventListener("input", updateFormState);
        yearElem.dispatchEvent(inputEvent);
    }
};

export function validateDob (
    form: HTMLFormElement,
    minAge: number | null = 18,
    maxAge: number | null = null,
    message: string = "You must be 18 or over to take out health insurance with Westfund.",
    isValidCallback?: Function
) {
    const dobFieldGroups = form.querySelectorAll('input[data-dob-group], select[data-dob-group]');
    const inputEvent = new Event('input');
    let firstLoad = true;

    const updateFormState = (event: Event) => {
        const target = event.target as HTMLInputElement | HTMLSelectElement;
        const dobGroup = target.getAttribute('data-dob-group');

        const dayElement = form.querySelector(`[data-dob-group="${dobGroup}"][data-dob-type="day"]`) as HTMLInputElement;
        const day = dayElement.value;
        const monthElement = form.querySelector(`[data-dob-group="${dobGroup}"][data-dob-type="month"]`) as HTMLSelectElement;
        const month = monthElement.value;
        const yearElement = form.querySelector(`[data-dob-group="${dobGroup}"][data-dob-type="year"]`) as HTMLInputElement;
        const year = yearElement.value;

        const hideAllErrorMessages = () => {
            hideErrorMessage(dayElement);
            hideErrorMessage(monthElement);
            hideErrorMessage(yearElement);
        }

        if(`${year}`.length != 4){
            showErrorMessage(yearElement, "Invalid year");
        }


        if (`${day}`.length > 0 && `${month}`.length == 2 && `${year}`.length == 4) {
            const now = Date.now();
            const dob = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            const age = calculateAge(dob);

            if(dob.getDate() !== parseInt(day)){
                showErrorMessage(dayElement, "Invalid date");
            }
            else if (target.closest('.hidden') === null) {
                if (dob.getTime() > now) {
                    showErrorMessage(target, "Date of birth must be today or earlier.");
                } else if (minAge !== null && age < minAge) {
                    showErrorMessage(target, message);
                } else if (maxAge !== null && age > maxAge) {
                    showErrorMessage(target, message);
                }else if(`${day}`.length > 2){
                    showErrorMessage(dayElement, "Invalid day");
                }
                else {
                    hideAllErrorMessages();
                    if(isValidCallback && !firstLoad){
                        isValidCallback(dob, target);
                    }
                }
            } else {
                hideAllErrorMessages();                
                if(isValidCallback && !firstLoad)
                    isValidCallback(dob, target);
            }
        }
    }

    dobFieldGroups?.forEach((elem) => {
        elem.addEventListener('input', updateFormState);

        elem.dispatchEvent(inputEvent);
    });
    firstLoad = false;
}

export function validatePattern (input: HTMLInputElement) {
    if (! input.hasAttribute('data-pattern') || ! input.hasAttribute('title')) {
        return;
    }
    const value = input.value;
    const regexString = input.getAttribute('data-pattern');
    const regex = new RegExp(regexString);

    if (value.length > 0 && value.match(regex) === null) {
        showErrorMessage(input, input.getAttribute('title'));
    } else {
        hideErrorMessage(input);
    }
}